import React, { ReactNode, HTMLAttributes } from 'react'
import classNames from 'classnames'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'body'
  | 'small'

export type TypographyColor =
  | 'primary'      // 基本テキスト色 (Gray 12 #202020)
  | 'secondary'    // セカンダリーテキスト色 (Gray 11 #646464)
  | 'tertiary'     // 注釈、補足情報 (Gray 9 #8D8D8D)
  | 'quaternary'   // ボーダー色 (Gray 7 #CECECE)
  | 'disabled'     // 無効状態のテキスト (Gray 8 #BBBBBB)
  | 'inverse'      // 濃い背景色上のテキスト (White #FFFFFF)
  | 'destructive'  // エラー、警告テキスト (Tomato 9 #E54D2E)
  | 'accent-primary'    // アクセントテキスト青 (Blue 10 #1495DC)
  | 'accent-secondary'  // アクセントテキスト赤 (Ruby 10 #EE5F87)
  | 'accent-tertiary'   // アクセントテキスト黄 (Amber 10 #FFBA18)
  | 'transparent'  // 半透明テキスト (Overlay White 7 rgba(255, 255, 255, 0.5))

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
  /**
   * テキストのバリエーション
   */
  variant?: TypographyVariant
  /**
   * テキストの色
   */
  color?: TypographyColor
  /**
   * テキストを太字にする
   */
  bold?: boolean
  /**
   * コンポーネントの子要素
   */
  children: ReactNode
  /**
   * タグを上書きする（デフォルトではvariantに基づいて決定）
   */
  component?: React.ElementType
}

/**
 * みてねのデザインシステムに準拠したタイポグラフィコンポーネント
 * Figmaのデザイン仕様に基づいて実装
 */
export const Typography: React.FC<TypographyProps> = ({
  variant = 'body',
  color = 'primary',
  bold = false,
  component,
  className,
  children,
  ...props
}) => {
  // variantに基づいてデフォルトのコンポーネントを決定
  const getDefaultComponent = () => {
    switch (variant) {
      case 'h1':
        return 'h1'
      case 'h2':
        return 'h2'
      case 'h3':
        return 'h3'
      case 'h4':
        return 'h4'
      case 'body':
        return 'p'
      case 'small':
        return 'span'
      default:
        return 'span'
    }
  }

  const Component = component || getDefaultComponent()

  // デスクトップとモバイルでフォントサイズを調整
  const variantStyles = {
    // デスクトップサイズ: md:以上の場合
    h1: 'text-[26px] md:text-[36px] leading-[1.4]',
    h2: 'text-[24px] md:text-[30px] leading-[1.4]',
    h3: 'text-[20px] md:text-[24px] leading-[1.4]',
    h4: 'text-[16px] md:text-[20px] leading-[1.4]',
    body: 'text-base leading-[1.6]', // 16px
    small: 'text-s leading-[1.6]', // 12.8px
  }

  const colorStyles = {
    primary: 'text-mi-text-dark',              // Gray 12 (#202020)
    secondary: 'text-mi-text-dark-gray',       // Gray 11 (#646464)
    tertiary: 'text-mi-text-gray',             // Gray 9 (#8D8D8D)
    quaternary: 'text-mi-text-quaternary',     // Gray 7 (#CECECE)
    disabled: 'text-mi-surface-disabled',      // Gray 8 (#BBBBBB)
    inverse: 'text-white',                     // White (#FFFFFF)
    destructive: 'text-mi-on-surface-destructive',  // Tomato 9 (#E54D2E)
    'accent-primary': 'text-mi-text-primary',       // Blue 10 (#1495DC)
    'accent-secondary': 'text-mi-on-surface-accent-secondary', // Ruby 10 (#EE5F87)
    'accent-tertiary': 'text-mi-accent-tertiary',   // Amber 10 (#FFBA18)
    'transparent': 'text-mi-white-overlay-50',       // Overlay White 7 (rgba(255, 255, 255, 0.5))
  }

  // Figmaでは日本語フォントとして、ヒラギノW3(Normal:300)とW6(Bold:600)しか選べないため、それらを利用している。
  // ただし、実装ではBootstrapと統一し、Normal:400、Bold:700を指定する。
  const fontWeightStyles = {
    bold: 'font-bold', // 700
    normal: 'font-normal', // 400
  }

  return (
    <Component
      className={classNames(
        variantStyles[variant],
        colorStyles[color],
        bold ? fontWeightStyles.bold : fontWeightStyles.normal,
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}
